import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { FormulaServices } from 'src/app/services/formula.service';
import { DataSourceConfig, FormulaDataSource, MockDataSource, ApiDataSource } from '../interfaces/data-source.models';

/**
 * DataSourceExecutorService
 * 
 * Serviço responsável por executar diferentes tipos de data sources:
 * - Formula: Reutiliza FormulaServices.executeFormula() existente
 * - Mock: Parseia JSON estático
 * - API: Executa request HTTP
 * 
 * Este serviço DELEGA para serviços existentes sempre que possível.
 */
@Injectable({
  providedIn: 'root'
})
export class DataSourceExecutorService {
  
  constructor(
    private formulaServices: FormulaServices,  // ✅ Reutiliza serviço existente
    private httpClient: HttpClient
  ) {}

  /**
   * Executa o data source configurado e retorna os dados como Observable
   * 
   * @param config Configuração do data source
   * @returns Observable com os dados retornados
   */
  execute(config: DataSourceConfig): Observable<any> {
    if (!config || config.type === 'none') {
      return of(null);
    }

    switch (config.type) {
      case 'formula':
        return this.executeFormula(config);
      case 'mock':
        return this.executeMock(config);
      case 'api':
        return this.executeApi(config);
      default:
        return throwError(() => new Error(`Tipo de data source desconhecido: ${(config as any).type}`));
    }
  }

  /**
   * Executa uma fórmula usando o FormulaServices existente
   * 
   * ✅ REUTILIZA FormulaServices.executeFormula()
   * 
   * @param config Configuração da fórmula
   * @returns Observable com resultado da fórmula (JSON)
   */
  private executeFormula(config: FormulaDataSource): Observable<any> {
    return from(
      this.formulaServices.executeFormula(config.expression, config.variables)
    ).pipe(
      map((resultado: any) => {
        try {
          // O resultado pode vir como string JSON ou já parseado
          return typeof resultado === 'string' ? JSON.parse(resultado) : resultado;
        } catch (error) {
          console.error('[DataSourceExecutor] Erro ao parsear resultado da fórmula:', error);
          throw new Error('Resultado da fórmula não é um JSON válido');
        }
      }),
      catchError(error => {
        console.error('[DataSourceExecutor] Erro ao executar fórmula:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Retorna dados mock (JSON estático)
   * 
   * @param config Configuração do mock
   * @returns Observable com dados parseados
   */
  private executeMock(config: MockDataSource): Observable<any> {
    try {
      const data = JSON.parse(config.jsonData);
      console.log('[DataSourceExecutor] Mock data carregado:', data);
      return of(data);
    } catch (error) {
      console.error('[DataSourceExecutor] Erro ao parsear JSON mock:', error);
      return throwError(() => new Error('JSON mock inválido'));
    }
  }

  /**
   * Executa request de API
   * 
   * @param config Configuração da API
   * @returns Observable com resposta da API
   */
  private executeApi(config: ApiDataSource): Observable<any> {
    let url = config.url;

    // Adiciona query params se existir
    if (config.queryParams && Object.keys(config.queryParams).length > 0) {
      const params = new URLSearchParams(config.queryParams);
      url += `?${params.toString()}`;
    }

    // Configura headers
    let headers = new HttpHeaders();
    if (config.headers) {
      Object.keys(config.headers).forEach(key => {
        headers = headers.set(key, config.headers![key]);
      });
    }

    const options = { headers };

    console.log(`[DataSourceExecutor] Executando ${config.method} ${url}`);

    // Executa request baseado no método
    switch (config.method) {
      case 'GET':
        return this.httpClient.get(url, options).pipe(
          catchError(error => this.handleApiError(error, config))
        );
      case 'POST':
        const postBody = config.body ? JSON.parse(config.body) : {};
        return this.httpClient.post(url, postBody, options).pipe(
          catchError(error => this.handleApiError(error, config))
        );
      case 'PUT':
        const putBody = config.body ? JSON.parse(config.body) : {};
        return this.httpClient.put(url, putBody, options).pipe(
          catchError(error => this.handleApiError(error, config))
        );
      case 'DELETE':
        return this.httpClient.delete(url, options).pipe(
          catchError(error => this.handleApiError(error, config))
        );
      default:
        return throwError(() => new Error(`Método HTTP não suportado: ${config.method}`));
    }
  }

  /**
   * Trata erros de API
   * 
   * @param error Erro recebido
   * @param config Configuração da API
   * @returns Observable com erro tratado
   */
  private handleApiError(error: any, config: ApiDataSource): Observable<never> {
    console.error('[DataSourceExecutor] Erro na chamada de API:', {
      url: config.url,
      method: config.method,
      error: error
    });

    let errorMessage = 'Erro ao executar request de API';
    
    if (error.error instanceof ErrorEvent) {
      // Erro do lado do cliente
      errorMessage = `Erro: ${error.error.message}`;
    } else {
      // Erro do lado do servidor
      errorMessage = `Erro ${error.status}: ${error.statusText || error.message}`;
    }

    return throwError(() => new Error(errorMessage));
  }
}

